// ABOUTME: Unified SmartQuoteAI component that presumes correctness and allows inline editing
// ABOUTME: Shows complete solution immediately with confidence-based UI states

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mic, MicOff, Camera, Edit3, Check, AlertTriangle, ChevronDown, ChevronUp,
  Send, Users, MessageSquare, MapPin, Sparkles, RefreshCw, X, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { debug } from '@/lib/utils';

// Import our AI utilities
import { speechToTextService, SpeechToTextResult } from './utils/speechToText';
import { analyzeUserInput, TradeMatch } from './utils/keywordMatcher';
import { generateSmartMessage, MessageGenerationOptions } from './utils/messageGenerator';
import { analyzePhoto, PhotoAnalysisResult, isPhotoCaptureSupported } from './utils/photoAnalysis';
import { Tradesperson } from '@/lib/types';
import { generateMessage, QuickContactTemplateData } from '@/lib/messaging/templates';
import { getTradeType } from '@/lib/tradeUtils';
import { formatPostcodeDisplay } from '@/lib/postcodeUtils';
import { useProfile } from '@/hooks/useProfile';

interface UnifiedSmartQuoteAIProps {
  tradespeople: Tradesperson[];
  userName?: string;
  userPostcode?: string;
  onComplete: (data: {
    message: string;
    selectedTradespeople: Tradesperson[];
    detectedCategory?: string;
    confidence: number;
  }) => void;
  className?: string;
}

type UIState = 'input' | 'ready' | 'almost-ready' | 'needs-info';

interface AnalysisState {
  matches: TradeMatch[];
  confidence: number;
  generatedMessage: string;
  suggestedTradespeople: Tradesperson[];
}

export function UnifiedSmartQuoteAI({
  tradespeople,
  userName = '',
  userPostcode = '',
  onComplete,
  className
}: UnifiedSmartQuoteAIProps) {
  const { profile: userProfile } = useProfile();

  // Input state
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoAnalysis, setPhotoAnalysis] = useState<PhotoAnalysisResult | null>(null);

  // Analysis state
  const [analysis, setAnalysis] = useState<AnalysisState>({
    matches: [],
    confidence: 0,
    generatedMessage: '',
    suggestedTradespeople: []
  });

  // UI state based on confidence
  const [uiState, setUIState] = useState<UIState>('input');
  const [hasUserStoppedInput, setHasUserStoppedInput] = useState(false);
  const [inputStableTimeout, setInputStableTimeout] = useState<NodeJS.Timeout | null>(null);

  // Editing state
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');
  const [showRematchSuggestion, setShowRematchSuggestion] = useState(false);
  const [originalJobType, setOriginalJobType] = useState('');

  // Processing state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);

  // Button send status tracking (tradesperson_id -> {email: sent_timestamp, message: sent_timestamp})
  const [buttonSentStatus, setButtonSentStatus] = useState<Record<string, {email?: number, message?: number}>>({});

  // Trade keyword detection
  const [matchedTradeCategories, setMatchedTradeCategories] = useState<string[]>([]);

  // Conservative trade-specific keyword matcher
  const detectTradeKeywords = useCallback((text: string) => {
    const lowerText = text.toLowerCase();

    const tradeKeywords = {
      plumber: ['boiler', 'radiator', 'tap', 'toilet', 'shower', 'drain', 'pipe', 'plumber', 'heating system', 'thermostat'],
      electrician: ['socket', 'outlet', 'switch', 'fuse', 'circuit breaker', 'electrician', 'electric heater', 'heat pump', 'wiring'],
      roofer: ['gutter', 'slate', 'roof tile', 'fascia', 'roofer', 'roof', 'chimney'],
      builder: ['builder', 'extension', 'wall', 'foundation', 'bricks', 'cement'],
      tiler: ['tiles', 'tiler', 'grouting', 'bathroom tiles', 'kitchen tiles'],
      carpenter: ['carpenter', 'wood', 'door', 'window', 'kitchen units', 'worktop', 'cabinet']
    };

    const matchedCategories: string[] = [];

    // Check which trade categories match
    for (const [category, keywords] of Object.entries(tradeKeywords)) {
      for (const keyword of keywords) {
        if (lowerText.includes(keyword)) {
          if (!matchedCategories.includes(category)) {
            matchedCategories.push(category);
          }
          break; // Move to next category once we have a match
        }
      }
    }

    return matchedCategories;
  }, []);

  // Check for trade keywords when input changes
  useEffect(() => {
    const combinedText = [inputText, photoAnalysis?.extractedText].filter(Boolean).join(' ');
    setMatchedTradeCategories(detectTradeKeywords(combinedText));
  }, [inputText, photoAnalysis, detectTradeKeywords]);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Determine UI state based on confidence and completeness
  useEffect(() => {
    if (!inputText.trim() && !photoAnalysis?.extractedText) {
      setUIState('input');
      setHasUserStoppedInput(false);
      return;
    }

    // Only transition away from input if we have trade keywords
    if (matchedTradeCategories.length === 0) {
      // Stay in input mode if no trade keywords detected
      return;
    }

    // Only transition if user has stopped inputting AND we have good analysis
    if (hasUserStoppedInput && analysis.confidence >= 0.7 && analysis.generatedMessage && analysis.suggestedTradespeople.length > 0) {
      setUIState('ready');
      setIsEditingMessage(true); // Auto-enable message editing when ready
    } else if (hasUserStoppedInput && analysis.confidence >= 0.4 && (analysis.generatedMessage || analysis.suggestedTradespeople.length > 0)) {
      setUIState('almost-ready');
      setIsEditingMessage(true); // Auto-enable message editing when almost ready
    } else if (hasUserStoppedInput) {
      setUIState('needs-info');
    }
    // If user hasn't stopped input, stay in 'input' mode regardless of analysis
  }, [analysis, inputText, photoAnalysis, hasUserStoppedInput, matchedTradeCategories]);

  // Perform comprehensive analysis
  const performAnalysis = useCallback(async (text: string, photo?: PhotoAnalysisResult) => {
    // Skip analysis if no trade keywords detected
    if (matchedTradeCategories.length === 0) {
      return;
    }
    if (!text.trim() && !photo?.extractedText) {
      setAnalysis({
        matches: [],
        confidence: 0,
        generatedMessage: '',
        suggestedTradespeople: []
      });
      return;
    }

    setIsAnalyzing(true);

    try {
      // Filter out invalid/null tradespeople before processing
      const validTradespeople = tradespeople.filter(t =>
        t && t.id && (t.name || t.org || t.email || t.phone)
      );

      debug.log('Filtered tradespeople:', {
        original: tradespeople.length,
        valid: validTradespeople.length,
        filtered: tradespeople.length - validTradespeople.length
      });

      // Combine text inputs
      const combinedText = [
        text,
        photo?.extractedText || '',
        photo?.detectedProblems.join(' ') || ''
      ].join(' ').trim();

      debug.log('Performing comprehensive analysis:', { combinedText });

      // Analyze for trade matches
      const textAnalysis = analyzeUserInput(combinedText);

      debug.log('Text analysis result:', {
        combinedText,
        matches: textAnalysis.matches,
        topMatch: textAnalysis.topMatch,
        keywords: textAnalysis.keywords
      });

      // Generate message
      const messageOptions: MessageGenerationOptions = {
        userName,
        userPostcode,
        originalText: combinedText,
        detectedCategory: textAnalysis.topMatch?.category,
        urgency: 'medium',
        preferredStyle: 'friendly'
      };

      const messageResult = generateSmartMessage(messageOptions);

      // Filter tradespeople by our keyword-matched categories
      let suggestedTrades: Tradesperson[] = [];

      if (matchedTradeCategories.length > 0) {
        // Show tradespeople that match any of our detected categories
        suggestedTrades = validTradespeople.filter(t => {
          const tradeType = (t.trade_type || t.category || '').toLowerCase();
          return matchedTradeCategories.some(category =>
            tradeType.includes(category.toLowerCase())
          );
        });

        // If no exact matches, show all tradespeople (but we detected keywords)
        if (suggestedTrades.length === 0) {
          suggestedTrades = validTradespeople;
        }
      }

      // Calculate overall confidence
      let overallConfidence = Math.max(
        textAnalysis.topMatch?.confidence || 0,
        messageResult.confidence,
        photo?.confidence || 0
      );

      // Boost confidence if we have good data across multiple inputs
      if (text.length > 20 && textAnalysis.matches.length > 0) {
        overallConfidence = Math.min(overallConfidence + 0.2, 1.0);
      }

      const newAnalysis: AnalysisState = {
        matches: textAnalysis.matches,
        confidence: overallConfidence,
        generatedMessage: messageResult.body,
        suggestedTradespeople: suggestedTrades
      };

      setAnalysis(newAnalysis);
      setEditedMessage(messageResult.body);

      // Store the original job type for comparison
      if (textAnalysis.topMatch?.category) {
        setOriginalJobType(textAnalysis.topMatch.category.id);
      }

      debug.log('Analysis completed:', {
        confidence: overallConfidence,
        matches: textAnalysis.matches.length,
        tradespeople: suggestedTrades.length,
        tradespeopleData: suggestedTrades.map(t => ({ id: t.id, name: t.name, trade_type: t.trade_type, category: t.category }))
      });

    } catch (error) {
      debug.error('Analysis failed:', error);
      toast.error('Analysis failed. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  }, [userName, userPostcode, tradespeople, matchedTradeCategories]);

  // Handle text input changes with proper debouncing for UI transitions
  useEffect(() => {
    // Clear any existing timeout
    if (inputStableTimeout) {
      clearTimeout(inputStableTimeout);
    }

    // Reset the "stopped input" flag when user is actively typing
    setHasUserStoppedInput(false);

    // Perform analysis immediately for real-time feedback (but don't transition UI yet)
    const analysisTimeoutId = setTimeout(() => {
      performAnalysis(inputText, photoAnalysis);
    }, 500);

    // Set a longer timeout to mark input as "stable" (user has stopped)
    const stableTimeoutId = setTimeout(() => {
      setHasUserStoppedInput(true);
    }, 1200); // 1.2 seconds after user stops typing

    setInputStableTimeout(stableTimeoutId);

    return () => {
      clearTimeout(analysisTimeoutId);
      clearTimeout(stableTimeoutId);
    };
  }, [inputText, photoAnalysis, performAnalysis]);

  // Voice input handlers
  const handleVoiceTranscript = useCallback((result: SpeechToTextResult) => {
    if (result.isFinal) {
      const newText = (inputText + ' ' + result.transcript).trim();
      setInputText(newText);

      // Auto-stop listening but let the normal debounce handle the transition
      setTimeout(() => {
        stopListening();
        // The transition will happen automatically via hasUserStoppedInput after 2 seconds
      }, 500);
    } else {
      // Show interim results in real-time without updating analysis
      if (textareaRef.current) {
        textareaRef.current.placeholder = `Hearing: "${result.transcript}"...`;
      }
    }
  }, [inputText]);

  const handleVoiceError = useCallback((error: string) => {
    toast.error(`Voice input failed: ${error}`);
    setIsListening(false);
    if (textareaRef.current) {
      textareaRef.current.placeholder = 'Describe what you need help with...';
    }
  }, []);

  const startListening = useCallback(async () => {
    setIsListening(true);
    // Clear any existing message editing state
    setIsEditingMessage(false);

    try {
      await speechToTextService.startListening(
        handleVoiceTranscript,
        handleVoiceError,
        {
          continuous: false, // Changed to false so it stops on natural pauses
          interimResults: true,
          language: 'en-GB'
        }
      );
    } catch (error) {
      debug.error('Failed to start listening:', error);
      setIsListening(false);
    }
  }, [handleVoiceTranscript, handleVoiceError]);

  const stopListening = useCallback(() => {
    speechToTextService.stopListening();
    setIsListening(false);
    if (textareaRef.current) {
      textareaRef.current.placeholder = 'Describe what you need help with...';
    }
  }, []);

  // Photo handlers
  const handlePhotoUpload = useCallback(async (file: File) => {
    setPhotoFile(file);
    setIsProcessingPhoto(true);

    try {
      const analysis = await analyzePhoto(file);
      setPhotoAnalysis(analysis);

      if (analysis.extractedText.trim()) {
        setInputText(prev => (prev + ' ' + analysis.extractedText).trim());
      }

      toast.success('Photo analyzed!');
    } catch (error) {
      debug.error('Photo analysis failed:', error);
      toast.error('Photo analysis failed');
    } finally {
      setIsProcessingPhoto(false);
    }
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handlePhotoUpload(file);
    }
  }, [handlePhotoUpload]);

  // Handle SMS messaging
  const handleMessage = useCallback((trade: Tradesperson) => {
    if (trade.phone) {
      // Use the AI-generated message directly
      const message = editedMessage || analysis.generatedMessage || 'Hi, I found you through TradesPurple and would like to discuss a job.';
      window.open(`sms:${trade.phone}?body=${encodeURIComponent(message)}`);

      // Track that this button was clicked
      setButtonSentStatus(prev => ({
        ...prev,
        [trade.id]: {
          ...prev[trade.id],
          message: Date.now()
        }
      }));
    } else {
      toast.info("No phone number available for SMS");
    }
  }, [editedMessage, analysis.generatedMessage]);

  // Handle email
  const handleEmail = useCallback((trade: Tradesperson) => {
    if (trade.email) {
      try {
        const tradeType = getTradeType(trade);
        const tradeTypeDisplay = tradeType ? tradeType.charAt(0).toUpperCase() + tradeType.slice(1) : 'tradesperson';

        // Generate subject and body using templates but use AI message as body
        const templateData: QuickContactTemplateData = {
          userName: userName || userProfile?.username || userProfile?.email?.split('@')[0] || 'Your name',
          postcode: userPostcode || userProfile?.postcode || '[POSTCODE]',
          tradeType: tradeTypeDisplay.toLowerCase(),
          isFromSavedList: false // This is a new contact via SmartQuote
        };

        const { subject: templateSubject } = generateMessage('quickContact', templateData);

        // Use the AI-generated message as the email body
        const emailBody = editedMessage || analysis.generatedMessage || `Hi there, I'm looking for a ${tradeTypeDisplay.toLowerCase()} and would like to discuss a job.`;

        const subject = encodeURIComponent(templateSubject);
        const body = encodeURIComponent(emailBody);

        window.open(`mailto:${trade.email}?subject=${subject}&body=${body}`);

        // Track that this button was clicked
        setButtonSentStatus(prev => ({
          ...prev,
          [trade.id]: {
            ...prev[trade.id],
            email: Date.now()
          }
        }));
      } catch (error) {
        debug.error('Error generating email template:', error);
        // Fallback email
        const fallbackSubject = encodeURIComponent(`Quote Request`);
        const fallbackBody = encodeURIComponent(editedMessage || analysis.generatedMessage || 'Hi, I would like to discuss a job.');
        window.open(`mailto:${trade.email}?subject=${fallbackSubject}&body=${fallbackBody}`);

        // Track that this button was clicked
        setButtonSentStatus(prev => ({
          ...prev,
          [trade.id]: {
            ...prev[trade.id],
            email: Date.now()
          }
        }));
      }
    } else {
      toast.info("No email address available");
    }
  }, [editedMessage, analysis.generatedMessage, userName, userProfile, userPostcode]);

  // Detect job type changes in message editing
  const detectJobTypeChange = useCallback((messageText: string) => {
    if (!originalJobType || !messageText.trim()) return false;

    // Simple keyword detection - look for new trade type keywords
    const currentAnalysis = analyzeUserInput(messageText);
    const newTopMatch = currentAnalysis.topMatch;

    if (newTopMatch && newTopMatch.confidence > 0.6) {
      const newJobType = newTopMatch.category.id;
      return newJobType !== originalJobType;
    }

    return false;
  }, [originalJobType]);

  // Handle message editing with job type change detection
  const handleMessageEdit = useCallback((newMessage: string) => {
    setEditedMessage(newMessage);

    // Check for job type changes
    if (detectJobTypeChange(newMessage)) {
      setShowRematchSuggestion(true);
    } else {
      setShowRematchSuggestion(false);
    }
  }, [detectJobTypeChange]);

  // Handle re-matching trades based on updated message
  const handleRematchTrades = useCallback(async () => {
    setShowRematchSuggestion(false);
    setIsAnalyzing(true);

    try {
      await performAnalysis(editedMessage, photoAnalysis);
    } finally {
      setIsAnalyzing(false);
    }
  }, [editedMessage, photoAnalysis, performAnalysis]);

  // Get confidence color and state
  const getConfidenceDisplay = () => {
    switch (uiState) {
      case 'ready':
        return { color: 'green', label: 'Ready to Send', icon: Check };
      case 'almost-ready':
        return { color: 'yellow', label: 'Almost Ready', icon: AlertTriangle };
      case 'needs-info':
        return { color: 'red', label: 'Need More Info', icon: AlertTriangle };
      default:
        return { color: 'gray', label: 'Getting Started', icon: Sparkles };
    }
  };

  const confidenceDisplay = getConfidenceDisplay();
  const ConfidenceIcon = confidenceDisplay.icon;

  return (
    <div className={cn("space-y-4 max-w-2xl mx-auto px-3", className)}>
      {/* Header with confidence state - only show during input */}
      {uiState === 'input' && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <ConfidenceIcon className={`h-6 w-6 text-${confidenceDisplay.color}-600`} />
            <h2 className="text-xl font-semibold text-purple-800">
              What do you need?
            </h2>
          </div>
        </div>
      )}

      {/* Input Section - hidden when we have matches (replaced by message editor) */}
      {uiState === 'input' && (
        <div className="space-y-3">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Describe the problem and any details, including timings."
              className={cn(
                "min-h-[200px] text-base pr-28 transition-colors",
                isListening && "bg-green-50 border-green-300"
              )}
            />

            {/* Voice and Camera buttons in textarea */}
            <div className="absolute bottom-3 right-3 flex space-x-2">
              {speechToTextService.isSupported() && (
                <Button
                  variant={isListening ? "default" : "outline"}
                  size="default"
                  onClick={isListening ? stopListening : startListening}
                  className={cn(
                    "p-3 transition-all",
                    isListening && "bg-purple-600 hover:bg-purple-700 text-white"
                  )}
                >
                  <Mic className={cn(
                    "h-5 w-5",
                    isListening && "text-white"
                  )} />
                </Button>
              )}

              {isPhotoCaptureSupported() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessingPhoto}
                  className="p-2"
                >
                  {isProcessingPhoto ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Thinking animation or no-match message */}
          {inputText.trim() && !hasUserStoppedInput && (
            <div className="flex items-center justify-center space-x-2 py-3 px-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span className="text-sm text-purple-700">Thinking...</span>
            </div>
          )}

          {/* No trade match message - shows when we can't detect any trade keywords */}
          {inputText.trim() && hasUserStoppedInput && matchedTradeCategories.length === 0 && (
            <div className="py-3 px-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 flex-1">
                  <AlertTriangle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-blue-800">
                    We can't match automatically, but continue and you can send the message to anyone from your list.
                  </span>
                </div>
                <Button
                  onClick={() => {
                    // Force transition to results view with all tradespeople
                    setUIState('ready');
                    setIsEditingMessage(true);
                    // Use their input with proper context for tradespeople
                    const userInput = inputText.trim();
                    const userMessage = userInput
                      ? `Hi, I'm looking for someone to help near ${userPostcode || '[YOUR POSTCODE]'}\n\n${userInput}\n\nCan you let me know any questions and availability?\n\nMany thanks,`
                      : `Hi, I'm looking for someone to help near ${userPostcode || '[YOUR POSTCODE]'}\n\nCan you let me know any questions and availability?\n\nMany thanks,`;

                    // Populate with all tradespeople since we couldn't match
                    setAnalysis(prev => ({
                      ...prev,
                      suggestedTradespeople: tradespeople.filter(t =>
                        t && t.id && (t.name || t.org || t.email || t.phone)
                      ),
                      generatedMessage: userMessage
                    }));
                    setEditedMessage(userMessage);
                  }}
                  size="sm"
                  className="ml-3 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Photo preview */}
          {photoFile && (
            <div className="flex items-center space-x-2 p-2 bg-blue-50 rounded border">
              <Camera className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-800">Photo: {photoFile.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPhotoFile(null);
                  setPhotoAnalysis(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="h-auto p-1 text-blue-600"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      )}


      {/* Analysis Results - shown when we have confidence */}
      {uiState !== 'input' && (
        <div className="flex flex-col h-[calc(100vh-16rem)] sm:h-[calc(100vh-12rem)] min-h-[450px]">
          {/* Generated Message Section */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
              <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Your Message
              </h2>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Reset to input mode - clear analysis and return to input
                    setUIState('input');
                    setAnalysis({
                      matches: [],
                      confidence: 0,
                      generatedMessage: '',
                      suggestedTradespeople: []
                    });
                    setEditedMessage('');
                    setIsEditingMessage(false);
                    setShowRematchSuggestion(false);
                    setOriginalJobType('');
                    setHasUserStoppedInput(false);
                    if (inputStableTimeout) {
                      clearTimeout(inputStableTimeout);
                      setInputStableTimeout(null);
                    }
                  }}
                  className="flex items-center space-x-1 text-gray-600"
                >
                  <span>Back to Input</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingMessage(!isEditingMessage)}
                  className="flex items-center space-x-1"
                >
                  <Edit3 className="h-3 w-3" />
                  <span>{isEditingMessage ? 'Done' : 'Edit'}</span>
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain">
              {isEditingMessage ? (
                <div className="h-full p-3 flex flex-col space-y-3">
                  <Textarea
                    value={editedMessage}
                    onChange={(e) => handleMessageEdit(e.target.value)}
                    className="flex-1 text-sm border-gray-200 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    placeholder="Edit your message..."
                  />

                {/* Job type change suggestion */}
                {showRematchSuggestion && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="h-4 w-4 text-blue-600" />
                        <span className="text-sm text-blue-800">
                          It looks like you might have changed the job type.
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowRematchSuggestion(false)}
                          className="text-blue-600 h-auto p-1"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRematchTrades}
                          className="text-blue-600 border-blue-300 hover:bg-blue-100"
                        >
                          Re-match trades?
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              ) : (
                <div className="h-full p-3">
                  <div className="h-full bg-gray-50 rounded-lg p-3 text-gray-900 whitespace-pre-wrap text-sm leading-normal overflow-y-auto">
                    {editedMessage || 'Message will appear here...'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tradespeople Section */}
          <div className="flex-shrink-0 h-[280px] flex flex-col border-t-2 border-gray-200">
            <div className="px-3 py-1.5 border-b border-gray-100">
              <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Send to Tradespeople
              </h2>
            </div>

            <div className="bg-white flex-1 overflow-y-auto overscroll-contain">
              {analysis.suggestedTradespeople.map((trade) => (
                <div
                  key={trade.id}
                  className="py-3 px-0 hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  {/* Horizontal layout: info on left, buttons on right */}
                  <div className="flex items-center justify-between">
                    {/* Left side: tradesperson info */}
                    <div className="flex-1 min-w-0 px-3">
                      <div className="font-medium text-gray-900 text-sm truncate">
                        {trade.name || trade.org || 'Unnamed Tradesperson'}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {trade.trade_type || trade.category || 'Unknown Trade'}
                      </div>
                    </div>

                    {/* Right side: action buttons */}
                    <div className="flex items-center space-x-2 pr-3">
                      {/* Show message button if they have phone */}
                      {trade.phone && (
                        <Button
                          size="sm"
                          className={cn(
                            "w-8 h-8 p-0 rounded-full text-white transition-all shadow-sm",
                            buttonSentStatus[trade.id]?.message
                              ? "bg-green-600 hover:bg-green-700 active:bg-green-800"
                              : "bg-green-500 hover:bg-green-600 active:bg-green-700"
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            handleMessage(trade);
                          }}
                        >
                          {buttonSentStatus[trade.id]?.message ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <MessageSquare className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                      {/* Show email button if they have email */}
                      {trade.email && (
                        <Button
                          size="sm"
                          className={cn(
                            "w-8 h-8 p-0 rounded-full text-white transition-all shadow-sm",
                            buttonSentStatus[trade.id]?.email
                              ? "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
                              : "bg-blue-500 hover:bg-blue-600 active:bg-blue-700"
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            handleEmail(trade);
                          }}
                        >
                          {buttonSentStatus[trade.id]?.email ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Send className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {analysis.suggestedTradespeople.length === 0 && (
                <p className="text-gray-500 text-center py-4">
                  No tradespeople found. Try describing your problem more specifically.
                </p>
              )}
            </div>
          </div>


        </div>
      )}

      {/* Loading States */}
      {isAnalyzing && (
        <div className="text-center py-4">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto text-purple-600" />
          <p className="text-sm text-purple-600 mt-2">Analyzing your request...</p>
        </div>
      )}
    </div>
  );
}
